import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Flex, ResponsiveSVG } from 'ui/acro'
import CommentAuthor from './CommentAuthor'
import ThumbsUp from '../../../assets/images/thumbup.svg'
import ThumbsUpSelected from '../../../assets/images/thumbupselected.svg'
import Text from 'ui/acro/typography/Text'
import ReportComment from './ReportComment'
import commentAxios from 'api/commentsAxios'
import { ReadMore, ReadMoreLegacy } from 'ui/acro'
import { useWindowResize } from 'hooks'

const ReplyWrapper = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  .toggle {
    font-size: 14px;
    color: #000;
    font-family: 'Proxima-Nova';
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.28px;
    text-transform: uppercase;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    cursor: pointer;
  }
`

const ReplyText = styled(Text)`
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.352px;
  width: 60%;
  @media (max-width: 768px) {
    width: 100%;
  }
`
const ActionItems = styled(Flex)`
  justify-content: space-between;
`

const ReportAction = styled(Text)`
  color: #636363;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  cursor: pointer;
`

const ReportedAction = styled(Text)`
  color: #636363;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.352px;
`
const Likes = styled(Flex)`
  gap: 4px;
  align-items: center;
`
const Options = styled(Flex)`
  gap: 12px;
`

const Option = styled.div`
  color: #636363;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  cursor: pointer;
`
// Comment Component
const Reply = ({
  reply,
  commentId,
  id,
  type,
  userName,
  setShowSnackbar,
  setReplies,
  onEdit,
  onDelete
}) => {
  const [showReportModal, setShowReportModal] = useState(false)
  const [userLiked, setUserLiked] = useState(reply?.likedByMe)
  const [likeCount, setLikeCount] = useState(reply?.likes)
  const [load, setLoad] = useState(false)

  const [reportCmt, setReportComt] = useState(false)
  const [lineCount, setLineCount] = useState(0)
  const { width } = useWindowResize()
  const replyRef = useRef(null)
  const isMobile = width <= 480

  useEffect(() => {
    setLikeCount(reply?.likes)
    setUserLiked(reply?.likedByMe)
  }, [reply?.likes])

  useEffect(() => {
    const div = replyRef.current
    if (div) {
      const computedStyle = window.getComputedStyle(div)
      const lineHeight = parseFloat(computedStyle.lineHeight)
      const height = div.offsetHeight
      const lines = Math.round(height / lineHeight)
      setLineCount(lines)
    }
  }, [])

  const getReadMoreInitialHeight = () => {
    if (!isMobile) {
      if (lineCount === 1) return 24
      if (lineCount === 2) return 48
      if (lineCount === 3) return 72
      if (lineCount >= 4) return 96
      return 96
    } else if (isMobile) {
      if (lineCount === 1) return 24
      if (lineCount === 2) return 48
      if (lineCount === 3) return 72
      if (lineCount === 4) return 96
      if (lineCount >= 5) return 120
      return 120
    }
  }

  const onLike = async () => {
    if (load) return
    let previous = {}
    setReplies((prev) =>
      prev.map((state) => {
        if (state.replyId !== reply.replyId) {
          return state
        }
        previous = { ...state }
        const currentLikes = state.likes || 0
        const likes = state.likedByMe ? currentLikes - 1 : currentLikes + 1
        return { ...state, likes, likedByMe: !state.likedByMe }
      })
    )
    try {
      setLoad(true)
      const response = await commentAxios.post(
        '/content-interaction/reaction',
        {
          content_type: type,
          content_id: id,
          type: 'LIKE',
          comment_id: commentId,
          reply_id: reply?.replyId
        }
      )
      if (response.status === 200) {
        setReplies((prev) =>
          prev.map((individualReply) =>
            individualReply.replyId === reply.replyId
              ? {
                  ...individualReply,
                  likedByMe: response?.data?.likedByMe,
                  likes: response?.data?.likes
                }
              : individualReply
          )
        )
      }
    } catch (err) {
      console.log(err)
      setReplies((prev) =>
        prev.map((state) => {
          if (state.replyId !== reply.replyId) {
            return state
          }
          return previous
        })
      )
    } finally {
      setLoad(false)
    }
  }

  const handleCloseReportModal = () => setShowReportModal(false)
  const handleShowReportModal = () => setShowReportModal(true)

  const reportState =
    reply.reported || reportCmt ? (
      <ReportedAction>Reported</ReportedAction>
    ) : (
      <ReportAction onClick={handleShowReportModal}>Report</ReportAction>
    )
  return (
    <ReplyWrapper>
      <CommentAuthor user={reply?.user} />
      {showReportModal && (
        <ReportComment
          show={showReportModal}
          closeHandler={handleCloseReportModal}
          id={id}
          reply={reply}
          commentId={commentId}
          type={type}
          setReportComt={setReportComt}
          setShowSnackbar={setShowSnackbar}
        />
      )}
      <ReadMoreLegacy
        initialHeight={getReadMoreInitialHeight()}
        noReadMoreHeightCompensator={0}
      >
        <ReplyText ref={replyRef}>{reply.text}</ReplyText>
      </ReadMoreLegacy>

      <ActionItems>
        <Likes>
          <ResponsiveSVG
            SVG={userLiked ? ThumbsUpSelected : ThumbsUp}
            style={{
              cursor: 'pointer'
            }}
            width='20px'
            height='20px'
            onClick={() => onLike()}
          />
          {likeCount > 0 && <>{likeCount}</>}
        </Likes>
        {userName === reply?.user?.username ? (
          <Options>
            <Option onClick={() => onEdit(reply.replyId)}>Edit</Option>
            <Option onClick={() => onDelete(reply.replyId)}>Delete</Option>
          </Options>
        ) : (
          <>{reportState}</>
        )}
        {/* {userName !== reply?.user?.username && <>{reported}</>} */}
      </ActionItems>
    </ReplyWrapper>
  )
}

export default Reply
